import { getCurrentTenant, type TenantId } from '@utils/tenant-utils'
import { ApiClient, ApiError } from './ApiClient'

export interface DocumentModel {
  id: string
  ownerId: string
  filename: string | null
  documentType: string
  description: string | null
  visibleForForestOwner: boolean
  isReadOnly?: boolean
  createdAt: string
  createdBy: string
  uploadedAt: string | null
  uploadedBy: string | null
}

export function getDocumentFilename(document?: DocumentModel | null): string {
  if (!document) return ''
  if (typeof document.filename === 'string' && document.filename.trim().length > 0) {
    return document.filename.trim()
  }
  return ''
}

export interface GetDocumentsParams {
  ownerId: string
  tenantId?: TenantId
  pageSize: number
  continuationToken?: string
  sort?: 'createdAt_desc' | 'createdAt_asc' | 'uploadedAt_desc' | 'uploadedAt_asc'
  dateFrom?: string
  dateTo?: string
  documentType?: string
}

export interface GetDocumentsResponse {
  documents: DocumentModel[]
  continuationToken: string
  hasMoreResults: boolean
}

export interface PostDocumentBody {
  ownerId: string
  type: string
  description: string | null
  visibleForForestOwner: boolean
}

export interface PutDocumentBody {
  ownerId: string
  type: string
  description: string | null
  visibleForForestOwner: boolean
}

export async function getDocuments(params: GetDocumentsParams): Promise<GetDocumentsResponse> {
  const queryParams: Record<string, string> = {
    ownerId: params.ownerId,
  }
  if (params.sort) queryParams.sort = params.sort
  if (params.dateFrom) queryParams.dateFrom = params.dateFrom
  if (params.dateTo) queryParams.dateTo = params.dateTo
  if (params.documentType) queryParams.documentType = params.documentType

  const headers: Record<string, string> = {
    'x-tenant-id': params.tenantId ?? getCurrentTenant(),
    'x-page-size': params.pageSize.toString(),
  }
  if (params.continuationToken) {
    headers['x-continuation-token'] = params.continuationToken
  }

  const response = await ApiClient.get<DocumentModel[]>('/documents', {
    headers,
    params: queryParams,
  })

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const continuationToken = response.headers.get('x-continuation-token')
  const hasMoreResults = response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'

  return {
    documents: response.data,
    continuationToken: continuationToken ?? '',
    hasMoreResults,
  }
}

export async function createDocument(body: PostDocumentBody): Promise<DocumentModel> {
  const tenantId = getCurrentTenant()
  const response = await ApiClient.post<DocumentModel>('/documents', body, {
    headers: { 'x-tenant-id': tenantId },
  })
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
  return response.data
}

export async function uploadDocumentContent(
  documentId: string,
  ownerId: string,
  file: File,
): Promise<DocumentModel> {
  const tenantId = getCurrentTenant()
  const formData = new FormData()
  formData.append('file', file)

  const response = await ApiClient.put<DocumentModel>(
    `/documents/${documentId}/content`,
    formData,
    {
      headers: { 'x-tenant-id': tenantId },
      params: { ownerId },
    },
  )
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
  return response.data
}

export async function updateDocument(id: string, body: PutDocumentBody): Promise<DocumentModel> {
  const tenantId = getCurrentTenant()
  const response = await ApiClient.put<DocumentModel>(`/documents/${id}`, body, {
    headers: { 'x-tenant-id': tenantId },
  })
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
  return response.data
}

export async function deleteDocument(ownerId: string, documentId: string): Promise<void> {
  const tenantId = getCurrentTenant()
  const response = await ApiClient.delete<void>('/documents', {
    headers: { 'x-tenant-id': tenantId },
    params: { ownerId, documentId },
  })
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
}

export async function downloadDocumentContent(
  documentId: string,
  ownerId: string,
  filename: string,
): Promise<void> {
  const tenantId = getCurrentTenant()
  const response = await ApiClient.get<Blob>(`/documents/${documentId}/content`, {
    headers: { 'x-tenant-id': tenantId },
    params: { ownerId },
    responseType: 'blob',
  })
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
  const url = URL.createObjectURL(response.data)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename || `document-${documentId}`
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
